"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import Image from "next/image";
import {
  Eye,
  EyeOff,
  ShieldCheck,
  Lock,
  CheckCircle2,
  AlertCircle,
  X,
  Scale,
  Clock,
  KeyRound,
  Mail,
} from "lucide-react";

const GoogleIcon = () => (
  <svg className="h-5 w-5 mr-2.5 shrink-0" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17Z"
    />
    <path
      fill="#34A853"
      d="M12 24c3.31 0 6.09-1.1 8.12-2.98l-3.88-3.05c-1.1.74-2.51 1.18-4.24 1.18-3.27 0-6.04-2.21-7.03-5.18H.94v3.16C3 21.2 7.21 24 12 24Z"
    />
    <path
      fill="#FBBC05"
      d="M4.97 14.02c-.25-.74-.39-1.54-.39-2.35s.14-1.61.39-2.35V6.16H.94C.34 7.36 0 8.74 0 10.17s.34 2.81.94 4.01l4.03-3.16Z"
    />
    <path
      fill="#EA4335"
      d="M12 4.75c1.8 0 3.42.62 4.69 1.83l3.52-3.52C18.09 1.14 15.31 0 12 0 7.21 0 3 2.8 .94 6.84l4.03 3.16C5.96 7.03 8.73 4.75 12 4.75Z"
    />
  </svg>
);

const MicrosoftIcon = () => (
  <svg className="h-5 w-5 mr-2.5 shrink-0" viewBox="0 0 21 21">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

export default function LoginPage() {
  const router = useRouter();

  // Mode: "lawyer" or "admin"
  const [authMode, setAuthMode] = useState<"lawyer" | "admin">("lawyer");

  // Form State
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successBanner, setSuccessBanner] = useState<string | null>(null);

  // Admin Approval State
  const [pendingApproval, setPendingApproval] = useState(false);
  const [approvalRequestId, setApprovalRequestId] = useState<string | null>(null);
  const [otpInput, setOtpInput] = useState("");
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  // Modals
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSuccess, setForgotSuccess] = useState(false);

  // Account Linking
  const [linkConfirmData, setLinkConfirmData] = useState<{ email: string; google_id: string } | null>(null);

  // URL query params
  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      if (params.get("mode") === "admin") {
        setAuthMode("admin");
      }
      if (params.get("registered") === "true") {
        setSuccessBanner("Account created successfully! Please sign in below.");
      }
      if (params.get("link_confirm") === "true") {
        const linkEmail = params.get("email") || "";
        const linkGoogleId = params.get("google_id") || "";
        if (linkEmail && linkGoogleId) {
          setEmail(linkEmail);
          setLinkConfirmData({ email: linkEmail, google_id: linkGoogleId });
          setSuccessBanner(`An account with ${linkEmail} already exists. Enter your password below to authorize linking your Google account.`);
        }
      }
      const errorParam = params.get("error");
      if (errorParam === "google_not_configured") {
        setErrorMsg("Google OAuth not configured — add GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET to .env");
      } else if (errorParam) {
        setErrorMsg("Authentication failed. Please try again.");
      }
    }
  }, []);

  // Poll Admin Approval
  useEffect(() => {
    if (!pendingApproval || !approvalRequestId) return;

    const interval = setInterval(async () => {
      try {
        const res = await fetch(`/api/auth/admin-login/status?requestId=${approvalRequestId}`);
        const data = await res.json();
        if (data.approved) {
          clearInterval(interval);
          window.location.href = data.redirect || "/admin";
        }
      } catch (err) {
        console.error("Polling approval error:", err);
      }
    }, 2500);

    return () => clearInterval(interval);
  }, [pendingApproval, approvalRequestId]);

  // Lawyer Sign In
  const handleLawyerSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMsg(null);

    try {
      if (linkConfirmData) {
        const res = await fetch("/api/auth/link-google", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: linkConfirmData.email,
            password,
            google_id: linkConfirmData.google_id,
          }),
        });
        const data = await res.json();
        if (!res.ok || data.error) {
          setErrorMsg(data.error || "Incorrect password. Account linking failed.");
        } else {
          window.location.href = "/dashboard";
        }
        return;
      }

      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();
      if (!res.ok || data.error) {
        setErrorMsg(data.error || "Invalid email or password.");
      } else {
        window.location.href = data.user?.role === "admin" ? "/admin" : "/dashboard";
      }
    } catch (err: any) {
      setErrorMsg(err.message || "Failed to sign in.");
    } finally {
      setIsLoading(false);
    }
  };

  // Admin Sign In
  const handleAdminSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMsg(null);
    setPendingApproval(false);

    try {
      const res = await fetch("/api/auth/admin-login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (!res.ok || data.error) {
        setErrorMsg(data.error || "Admin authentication failed.");
        return;
      }

      if (data.success && data.redirect) {
        window.location.href = data.redirect;
        return;
      }

      if (data.pending_approval) {
        setPendingApproval(true);
        setApprovalRequestId(data.requestId);
        setSuccessBanner(`Approval request dispatched to Superadmin (muhammad.nouman@ml1.ai)`);
      }
    } catch (err: any) {
      setErrorMsg(err.message || "Failed to process admin sign in.");
    } finally {
      setIsLoading(false);
    }
  };

  // OTP Verification
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpInput || !approvalRequestId) return;
    setIsVerifyingOtp(true);
    setErrorMsg(null);

    try {
      const res = await fetch("/api/auth/admin-login/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ requestId: approvalRequestId, otpCode: otpInput }),
      });
      const data = await res.json();

      if (!res.ok || data.error) {
        setErrorMsg(data.error || "Invalid OTP code.");
      } else {
        window.location.href = data.redirect || "/admin";
      }
    } catch (err: any) {
      setErrorMsg(err.message || "Failed to verify OTP.");
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleForgotSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setForgotSuccess(true);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col justify-between selection:bg-slate-900 selection:text-white font-sans text-slate-900">
      {/* Top Banner / Logo */}
      <header className="w-full px-6 py-6 flex items-center justify-between max-w-7xl mx-auto">
        <Link href="/" className="flex items-center gap-2 group">
          <div className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center font-bold text-base">
            🍁
          </div>
          <span className="font-bold text-xl tracking-tight text-slate-900">
            CanLexa
          </span>
        </Link>
      </header>

      {/* Main Login Card */}
      <main className="flex-1 flex items-center justify-center p-4 sm:p-6 my-4">
        <div className="w-full max-w-[420px] bg-white border border-slate-200/90 rounded-2xl p-7 sm:p-8 shadow-sm">
          {/* Dual Tab Switcher */}
          <div className="grid grid-cols-2 gap-1 p-1 bg-slate-100 rounded-xl mb-6 text-xs font-semibold">
            <button
              type="button"
              onClick={() => {
                setAuthMode("lawyer");
                setErrorMsg(null);
                setPendingApproval(false);
              }}
              className={`py-2 rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                authMode === "lawyer"
                  ? "bg-white text-slate-900 shadow-xs"
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              <Scale size={14} />
              <span>Lawyer Login</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setAuthMode("admin");
                setErrorMsg(null);
                setPendingApproval(false);
              }}
              className={`py-2 rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                authMode === "admin"
                  ? "bg-[#0F172A] text-white shadow-xs"
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              <ShieldCheck size={14} />
              <span>Admin Login</span>
            </button>
          </div>

          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">
              {authMode === "admin" ? "Admin Portal" : "Sign in to CanLexa"}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {authMode === "admin"
                ? "Restricted administration console"
                : "Enter your legal work credentials to continue"}
            </p>
          </div>

          {/* Superadmin Note in Admin Mode */}
          {authMode === "admin" && !pendingApproval && (
            <div className="mb-4 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-700 flex items-start gap-2">
              <KeyRound size={15} className="text-slate-900 shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold text-slate-900">Superadmin:</span> muhammad.nouman@ml1.ai
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Other admin requests require Superadmin email approval.
                </div>
              </div>
            </div>
          )}

          {/* Alerts */}
          {errorMsg && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs flex items-center gap-2">
              <AlertCircle size={15} className="shrink-0 text-red-500" />
              <span>{errorMsg}</span>
            </div>
          )}

          {successBanner && (
            <div className="mb-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs flex items-center gap-2">
              <CheckCircle2 size={15} className="shrink-0 text-emerald-600" />
              <span>{successBanner}</span>
            </div>
          )}

          {/* Pending Approval Screen */}
          {pendingApproval ? (
            <div className="space-y-4 pt-1">
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-center space-y-2">
                <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center mx-auto text-slate-800">
                  <Clock size={20} className="animate-spin" />
                </div>
                <div className="text-sm font-bold text-slate-900">Approval Dispatched</div>
                <p className="text-xs text-slate-600">
                  An access approval notification for <strong>{email}</strong> has been sent to:
                </p>
                <div className="inline-block px-2.5 py-1 bg-white border border-slate-300 rounded text-xs font-mono font-semibold text-slate-800">
                  muhammad.nouman@ml1.ai
                </div>
                <p className="text-[11px] text-slate-500">
                  Waiting for Superadmin approval... Screen will automatically redirect.
                </p>
              </div>

              {/* OTP Input Form */}
              <form onSubmit={handleVerifyOtp} className="space-y-2 pt-1">
                <label className="block text-xs font-medium text-slate-700">
                  Or enter 6-digit OTP code:
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    maxLength={6}
                    placeholder="123456"
                    value={otpInput}
                    onChange={(e) => setOtpInput(e.target.value)}
                    className="h-10 flex-1 px-3 border border-slate-300 rounded-lg text-center font-mono text-sm tracking-widest text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0F172A]"
                  />
                  <button
                    type="submit"
                    disabled={isVerifyingOtp || !otpInput}
                    className="px-4 h-10 rounded-lg bg-[#0F172A] hover:bg-[#1E293B] text-white text-xs font-semibold disabled:opacity-50 transition-all cursor-pointer"
                  >
                    {isVerifyingOtp ? "Verifying..." : "Verify OTP"}
                  </button>
                </div>
              </form>

              <button
                type="button"
                onClick={() => setPendingApproval(false)}
                className="w-full py-2 text-xs font-medium text-slate-500 hover:text-slate-800 transition-colors cursor-pointer text-center"
              >
                Cancel and try again
              </button>
            </div>
          ) : (
            /* Standard Login Form */
            <form onSubmit={authMode === "admin" ? handleAdminSignIn : handleLawyerSignIn} className="space-y-4">
              {/* OAuth Buttons (Only in Lawyer mode) */}
              {authMode === "lawyer" && (
                <>
                  <div className="space-y-2.5 mb-5">
                    <button
                      type="button"
                      onClick={() => (window.location.href = "/api/auth/google/login")}
                      className="w-full h-11 flex items-center justify-center px-4 border border-slate-300 rounded-lg text-sm font-semibold text-slate-700 bg-white hover:bg-slate-50 transition-colors shadow-xs cursor-pointer"
                    >
                      <GoogleIcon />
                      <span>Continue with Google</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => (window.location.href = "/api/auth/google/login")}
                      className="w-full h-11 flex items-center justify-center px-4 border border-slate-300 rounded-lg text-sm font-semibold text-slate-700 bg-white hover:bg-slate-50 transition-colors shadow-xs cursor-pointer"
                    >
                      <MicrosoftIcon />
                      <span>Continue with Microsoft</span>
                    </button>
                  </div>

                  <div className="relative my-4 text-center">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-slate-200" />
                    </div>
                    <span className="relative px-3 bg-white text-xs uppercase tracking-wider text-slate-400 font-medium">
                      or
                    </span>
                  </div>
                </>
              )}

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  {authMode === "admin" ? "Admin Email Address" : "Work email"}
                </label>
                <input
                  type="email"
                  required
                  placeholder={authMode === "admin" ? "muhammad.nouman@ml1.ai" : "you@lawfirm.ca"}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11 block w-full px-3.5 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0F172A] focus:border-transparent transition-all"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-semibold text-slate-700">
                    Password
                  </label>
                  {authMode === "lawyer" && (
                    <button
                      type="button"
                      onClick={() => setShowForgotModal(true)}
                      className="text-xs text-slate-500 hover:text-slate-900 cursor-pointer"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    placeholder="••••••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 block w-full px-3.5 pr-11 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0F172A] focus:border-transparent transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 transition-colors z-10 cursor-pointer"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="mt-5 h-11 w-full flex items-center justify-center py-3 px-4 rounded-lg text-sm font-semibold text-white bg-[#0F172A] hover:bg-[#1E293B] active:bg-[#090E17] focus:outline-none focus:ring-2 focus:ring-[#0F172A] focus:ring-offset-2 transition-all shadow-xs disabled:opacity-60 cursor-pointer"
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Signing in...</span>
                  </div>
                ) : authMode === "admin" ? (
                  "Sign in to Admin Console"
                ) : (
                  "Continue with email"
                )}
              </button>
            </form>
          )}

          {/* Footer link in card */}
          {authMode === "lawyer" && (
            <div className="mt-6 text-center text-xs text-slate-500 border-t border-slate-100 pt-4">
              Don&apos;t have an account?{" "}
              <Link href="/signup" className="text-slate-900 font-semibold hover:underline">
                Sign up
              </Link>
            </div>
          )}
        </div>
      </main>

      {/* Security Footer */}
      <footer className="w-full text-center text-xs py-5 text-slate-500 border-t border-slate-200/60 bg-white/50">
        <p className="flex items-center justify-center gap-1.5 flex-wrap px-4">
          <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0" />
          <span>Your confidential information is protected.</span>{" "}
          <button
            type="button"
            onClick={() => setShowSecurityModal(true)}
            className="text-slate-900 hover:underline font-semibold inline-flex items-center gap-0.5 cursor-pointer"
          >
            Read more
          </button>
          .
        </p>
      </footer>

      {/* Security Info Modal */}
      {showSecurityModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-xs p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-200 animate-fadeIn">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-emerald-600" />
                <h3 className="text-lg font-bold text-slate-900">
                  CanLexa Enterprise Security
                </h3>
              </div>
              <button
                onClick={() => setShowSecurityModal(false)}
                className="text-slate-400 hover:text-slate-600 rounded-lg p-1 transition-colors cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4 text-sm text-slate-600 leading-relaxed">
              <div className="flex items-start gap-3">
                <Lock className="w-5 h-5 text-slate-900 shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-slate-900">
                    End-to-End Encryption
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    All case files and firm communications are encrypted using
                    AES-256 at rest and TLS 1.3 in transit.
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Scale className="w-5 h-5 text-slate-900 shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-slate-900">
                    Law Society &amp; PIPEDA Compliance
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Built to satisfy Ontario Law Society confidentiality rules.
                    Zero customer data is retained by LLM providers for training.
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-slate-900">
                    Superadmin Supervised Access
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Administrative credentials and tenant permissions are strictly
                    supervised by <strong>muhammad.nouman@ml1.ai</strong>.
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-3 border-t border-slate-100 flex justify-end">
              <button
                onClick={() => setShowSecurityModal(false)}
                className="px-5 py-2 rounded-lg text-sm font-semibold bg-[#0F172A] text-white hover:bg-[#1E293B] transition-colors cursor-pointer"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-xs p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-200 animate-fadeIn">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
              <h3 className="text-lg font-bold text-slate-900">
                Reset your password
              </h3>
              <button
                onClick={() => setShowForgotModal(false)}
                className="text-slate-400 hover:text-slate-600 rounded-lg p-1 transition-colors cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            {forgotSuccess ? (
              <div className="py-6 flex flex-col items-center text-center gap-3">
                <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
                  <CheckCircle2 size={28} />
                </div>
                <div className="font-bold text-slate-900">
                  Password reset link sent!
                </div>
                <p className="text-xs text-slate-500 max-w-xs">
                  We&apos;ve sent instructions to <strong>{forgotEmail}</strong> if
                  an account exists with that address.
                </p>
                <button
                  onClick={() => setShowForgotModal(false)}
                  className="mt-4 px-6 py-2.5 rounded-lg text-sm font-semibold bg-[#0F172A] text-white hover:bg-[#1E293B] transition-colors cursor-pointer"
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotSubmit} className="space-y-4">
                <p className="text-xs text-slate-500 leading-relaxed">
                  Enter your firm work email and we&apos;ll send you instructions
                  to reset your password.
                </p>
                <div>
                  <label
                    htmlFor="forgot_email"
                    className="block text-sm font-medium text-slate-700 mb-1"
                  >
                    Work email
                  </label>
                  <input
                    id="forgot_email"
                    type="email"
                    required
                    placeholder="you@lawfirm.ca"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className="h-11 block w-full px-4 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0F172A] focus:border-transparent"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowForgotModal(false)}
                    className="px-4 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-[#0F172A] text-white hover:bg-[#1E293B] transition-colors cursor-pointer"
                  >
                    Send reset instructions
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
